<script setup lang="ts">
// A destination's form (EditDrawer): where an encode goes (push, pull,
// SRT, UDP, a recording), with its delay. Through central it fills itself
// from a broadcaster's feed in the directory (a copy, remembered as its
// origin: DECISIONS 2026-10-06 "broadcaster directory").
import { computed, onMounted, reactive, ref, watch } from 'vue'
import {
    api,
    ApiError,
    base,
    central,
    type Broadcaster,
    type BroadcasterFeed,
    type Destination,
    type EditTarget,
    type Encode,
} from '../../api'
import { destinationUse, isRecommended, recommended } from '../../encoding'
import { freePort, gbText, joinRTMP, newId, ristListenPort, slug, splitRTMP, streamNameError } from '../../util'
import { isTwitchIngest, youtubeBackup } from '../../platforms'
import FolderPicker from '../FolderPicker.vue'
import SecretInput from '../SecretInput.vue'
import { clone, useEditContext, usePorts } from './context'
import RistOptions from './RistOptions.vue'
import ScheduleEditor from './ScheduleEditor.vue'

const props = defineProps<{ target: EditTarget }>()
const t = props.target
const { node, cfg, ipv4, isNew } = useEditContext(t)
function blankDest(encodeId?: string): Destination {
    return {
        id: '',
        name: '',
        encode_id: encodeId ?? cfg.value.encodes[0]?.id ?? '',
        kind: 'rtmp_push',
        enabled: false,
        delay_seconds: 0,
        url: 'rtmp://',
        srt: { latency_ms: 200 },
        path: '/var/lib/ese/recordings',
        segment_seconds: 600,
        keep_days: 7,
    }
}
const dest = reactive<Destination>(
    t.kind === 'destination'
        ? clone(cfg.value.destinations.find((d) => d.id === t.id) ?? blankDest(t.encodeId))
        : blankDest(),
)
dest.interface ??= ''
// an RTMP push's URL as its server and its stream key, the key masked
// (DECISIONS 2026-10-06 "broadcaster directory", answer 1); saved joined
const rtmpServer = ref('')
const rtmpKey = ref('')
function splitURL() {
    ;({ server: rtmpServer.value, key: rtmpKey.value } = splitRTMP(dest.url ?? ''))
}
splitURL()
watch(
    () => dest.kind,
    (k) => k === 'rtmp_push' && splitURL(),
)
watch([rtmpServer, rtmpKey], () => {
    if (dest.kind === 'rtmp_push') dest.url = joinRTMP(rtmpServer.value, rtmpKey.value)
})
const templateId = ref('')
function applyTemplate() {
    const tp = cfg.value.templates?.find((x) => x.id === templateId.value)
    if (tp) {
        dest.kind = tp.kind
        dest.url = tp.url
        splitURL()
        dest.srt = clone(tp.srt ?? {})
    }
}
// the broadcaster directory (central's; none on a node opened directly)
const broadcasters = ref<Broadcaster[]>([])
onMounted(async () => {
    if (!base) return
    try {
        broadcasters.value = await central.broadcasters()
    } catch {
        /* central unreachable: no directory */
    }
})
const pickB = ref<number | ''>(dest.origin?.broadcaster ?? '')
const pickedB = computed(() => broadcasters.value.find((b) => b.id === pickB.value))
// a feed fills the form: its kind, address, key or credentials; the name
// when none is typed (or it was the last feed's); and the origin
function applyFeed(b: Broadcaster, f: BroadcasterFeed) {
    const d = f.delivery
    if (d.kind === 'other') return
    dest.kind = d.kind
    if (d.kind === 'rtmp_push') {
        dest.url = joinRTMP(d.url ?? '', d.key ?? '')
        splitURL()
    } else if (d.kind === 'srt_caller' || d.kind === 'udp') {
        dest.url = d.url ?? ''
    }
    if (d.kind === 'srt_caller' || d.kind === 'srt_listener') {
        dest.srt = {
            latency_ms: d.kind === 'srt_caller' ? d.srt?.latency_ms || 200 : undefined, // a listener's readers set theirs
            pbkeylen: d.srt?.pbkeylen || undefined,
            streamid: d.kind === 'srt_caller' ? d.srt?.streamid || undefined : undefined,
            passphrase: d.srt?.passphrase || undefined,
        }
    }
    if (d.kind === 'rtmp_pull' || d.kind === 'srt_listener') {
        dest.stream = d.stream || undefined
        dest.max_clients = d.max_clients || undefined
        pullUser.value = d.user ?? ''
        pullPass.value = d.pass ?? ''
    }
    const auto = dest.origin ? `${dest.origin.broadcaster_name} · ${dest.origin.feed_name}`.slice(0, 120) : ''
    if (!dest.name.trim() || dest.name === auto) dest.name = `${b.name} · ${f.description}`.slice(0, 120)
    dest.origin = { broadcaster: b.id, broadcaster_name: b.name, feed: f.id, feed_name: f.description }
}
function pickFeed(id: string) {
    const b = pickedB.value
    const f = b?.feeds.find((x) => x.id === id)
    if (b && f) applyFeed(b, f)
}
// the feed it came from, as the directory has it now (its notes)
const originFeed = computed(() =>
    broadcasters.value.find((b) => b.id === dest.origin?.broadcaster)?.feeds.find((f) => f.id === dest.origin?.feed),
)
const isFile = computed(() => dest.kind === 'file')
// a Twitch Enhanced Broadcasting encode goes to Twitch (where Twitch says)
// or to a recording; no other encode can use that kind
const destEncodeIsTwitch = computed(() => !!cfg.value.encodes.find((e) => e.id === dest.encode_id)?.twitch)
watch(
    destEncodeIsTwitch,
    (tw) => {
        if (tw && dest.kind !== 'file') dest.kind = 'twitch_eb'
        else if (!tw && dest.kind === 'twitch_eb') dest.kind = 'rtmp_push'
    },
    { immediate: true },
)
// an encode going to a platform that is not on the encode lab's settings
// (DECISIONS 2026-10-09 "encode guidance"): one click fills them into it
const destEncode = computed(() => cfg.value.encodes.find((e) => e.id === dest.encode_id))
const offerRecommended = computed(() => {
    const e = destEncode.value
    if (!e || e.passthrough || e.twitch || isRecommended(e.video, e.advanced)) return false
    if (e.video.codec !== 'h264_nvenc' && !(e.video.codec === 'libx264' && (node.status?.machine.gpus?.length ?? 0)))
        return false
    const others = cfg.value.destinations.filter((d) => d.encode_id === e.id && d.id !== dest.id)
    return destinationUse([...others, dest])?.use === 'platform'
})
async function applyRecommended() {
    try {
        await node.patch<Encode>('encodes', dest.encode_id, (cur) => ({
            ...cur,
            ...recommended(cur.video, cur.advanced),
        }))
    } catch {
        /* cancelled, or the store said why */
    }
}
// a passthrough carries its source's audio, which MP4 may not take: TS only
const destEncodeIsPassthrough = computed(() => !!cfg.value.encodes.find((e) => e.id === dest.encode_id)?.passthrough)
// recording folder: the picker, and a line on what is at the path now
const picking = ref(false)
const pathNote = ref<{ text: string; bad?: boolean } | null>(null)
let pathTimer = 0
function checkPath() {
    clearTimeout(pathTimer)
    pathNote.value = null
    if (!isFile.value || !dest.path?.startsWith('/')) return
    pathTimer = window.setTimeout(async () => {
        try {
            const d = await api.fs(dest.path!)
            const free = gbText(d.free_gb)
            pathNote.value = d.writable
                ? { text: `exists · ${free} free` }
                : { text: 'exists, but the node cannot write to it', bad: true }
        } catch (e) {
            if (e instanceof ApiError && e.status === 404)
                pathNote.value = { text: 'does not exist yet: created when the recording starts' }
            else pathNote.value = { text: e instanceof Error ? e.message : String(e), bad: true }
        }
    }, 400)
}
watch(() => [dest.kind, dest.path], checkPath, { immediate: true })
// rtmp_pull / srt_listener / hls_pull: the node serves the stream; optional reader credentials
const isPull = computed(() => dest.kind === 'rtmp_pull' || dest.kind === 'srt_listener' || dest.kind === 'hls_pull')
// a 1+1 pair (DECISIONS 2026-10-10 "1+1: a destination's backup address"):
// where the partner sends a destination that sends to an address; empty,
// both send to this one (a receiver that takes either)
const partnerKinds = ['rtmp_push', 'srt_caller', 'udp', 'rist', 'rtp', 'hls_push']
const hasPartner = computed(() => !!node.pair && partnerKinds.includes(dest.kind))
const ownURL = computed(() =>
    dest.kind === 'rtmp_push' ? joinRTMP(rtmpServer.value, rtmpKey.value) : (dest.url ?? ''),
)
const ytBackup = computed(() => youtubeBackup(ownURL.value))
const twitchPaired = computed(() => !!node.pair && (dest.kind === 'twitch_eb' || isTwitchIngest(ownURL.value)))
// a pull kind's protocol and the node's default port for it
const pullKind = computed((): { proto: 'udp' | 'tcp'; def: number } =>
    dest.kind === 'srt_listener'
        ? { proto: 'udp', def: pullDef.value.srt }
        : dest.kind === 'hls_pull'
          ? { proto: 'tcp', def: pullDef.value.hls ?? 8888 }
          : { proto: 'tcp', def: pullDef.value.rtmp },
)
const pullUser = ref(dest.pull_auth?.user ?? '')
const pullPass = ref(dest.pull_auth?.pass ?? '')
// ports: what this destination would listen on, and who else listens on this node
const myPort = computed((): { proto: 'udp' | 'tcp'; port: number } | null => {
    if (dest.kind === 'rist') return ristListenPort(dest.url) ? { proto: 'udp', port: ristListenPort(dest.url) } : null
    if (!isPull.value) return null
    return { proto: pullKind.value.proto, port: Number(dest.port) || pullKind.value.def }
})
const { pullDef, otherPorts, portClash, portNote } = usePorts(t, myPort)
// a new pull destination gets a free port straight away (the default when that is free)
watch(
    () => dest.kind,
    () => {
        if (!isNew.value || !isPull.value) return
        const { proto, def } = pullKind.value
        const p = freePort(otherPorts.value, proto, def)
        dest.port = p === def ? undefined : p
    },
    { immediate: true },
)
watch(
    () => dest.kind,
    (k) => {
        if (k === 'rist') dest.rist ??= {}
    },
    { immediate: true },
)

// hls_push: the stream's SCTE-35 cues as playlist tags ('' none)
const hlsCues = computed({
    get: () => dest.hls_cues ?? '',
    set: (v) => (dest.hls_cues = v || undefined),
})
// rtp: 2022-1 FEC and a second path (2022-7), off unless set
const fecOn = ref(!!dest.fec)
const fecCols = ref(dest.fec?.columns ?? 10)
const fecRows = ref(dest.fec?.rows ?? 10)
const path2On = ref(!!dest.path2)
watch(path2On, (on) => {
    if (on) dest.path2 ??= { url: '' }
})
async function save() {
    const id = dest.id || newId(slug(dest.name) + '-', new Set(cfg.value.destinations.map((d) => d.id)))
    const pull = isPull.value
    const body = {
        ...dest,
        id,
        delay_seconds: Number(dest.delay_seconds),
        adaptive: (dest.kind === 'hls_push' && dest.adaptive) || undefined,
        hls_cues: (dest.kind === 'hls_push' && !dest.adaptive && dest.hls_cues) || undefined,
        schedule: dest.schedule?.length ? dest.schedule : undefined,
        srt:
            dest.kind === 'srt_caller' || dest.kind === 'srt_listener'
                ? { ...dest.srt, bitrate_k: Number(dest.srt?.bitrate_k) || undefined }
                : undefined,
        rist: dest.kind === 'rist' ? dest.rist : undefined,
        url: dest.kind === 'file' || dest.kind === 'twitch_eb' || pull ? undefined : dest.url,
        ttl: (dest.kind === 'udp' || dest.kind === 'rtp') && dest.ttl ? Number(dest.ttl) : undefined,
        interface: (dest.kind === 'udp' || dest.kind === 'rtp') && dest.interface ? dest.interface : undefined,
        fec:
            dest.kind === 'rtp' && fecOn.value
                ? { columns: Number(fecCols.value), rows: Number(fecRows.value) }
                : undefined,
        path2:
            dest.kind === 'rtp' && path2On.value && dest.path2?.url
                ? { url: dest.path2.url, interface: dest.path2.interface || undefined }
                : undefined,
        path: dest.kind === 'file' ? dest.path : undefined,
        pull_auth:
            pull && pullUser.value && pullPass.value ? { user: pullUser.value, pass: pullPass.value } : undefined,
        max_clients: pull && dest.kind !== 'hls_pull' && dest.max_clients ? Number(dest.max_clients) : undefined,
        port: pull && dest.port ? Number(dest.port) : undefined,
        stream: pull && dest.stream?.trim() ? dest.stream.trim() : undefined,
        segment_seconds:
            dest.kind === 'file' && dest.segment_seconds && !dest.open_ended ? Number(dest.segment_seconds) : undefined,
        container: dest.kind === 'file' && dest.container === 'mp4' ? 'mp4' : undefined,
        open_ended: (dest.kind === 'file' && dest.open_ended) || undefined,
        keep_days: dest.kind === 'file' && dest.keep_days ? Number(dest.keep_days) : undefined,
        origin: dest.origin ?? undefined,
        partner_url:
            hasPartner.value && !dest.pair_only && dest.partner_url?.trim() ? dest.partner_url.trim() : undefined,
        pair_only: node.pair && dest.pair_only ? dest.pair_only : undefined,
    }
    await node.save('destinations', id, body)
    node.select({ kind: 'destination', id })
}
// what the node would refuse, said before Save (WEB-37)
const streamProblem = computed(() => (isPull.value ? streamNameError(dest.stream ?? '') : ''))
defineExpose({
    save,
    snapshot: () => JSON.stringify([dest, pullUser.value, pullPass.value]),
    problem: () => streamProblem.value,
})
</script>

<template>
    <div class="mb-2.5">
        <label>Name</label><input v-model="dest.name" required autofocus placeholder="Twitch main" />
    </div>
    <div class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Encode</label
            ><select v-model="dest.encode_id">
                <option v-for="e in cfg.encodes" :key="e.id" :value="e.id">{{ e.name }}</option>
            </select>
            <div v-if="offerRecommended" class="mt-1 text-xs text-fg-3" data-test="dest-recommended">
                For a platform the encode lab recommends NVENC p7 with lookahead and temporal AQ: a better picture for
                about 0.5 s more delay, no CPU.
                <template v-if="destEncode?.video.codec === 'libx264'"
                    >{{ destEncode.name }} encodes on x264 {{ destEncode.video.preset }}.</template
                >
                <button type="button" class="btn-ghost text-xs" @click="applyRecommended">
                    {{ destEncode?.video.codec === 'libx264' ? 'Move' : 'Apply to' }} {{ destEncode?.name
                    }}{{ destEncode?.video.codec === 'libx264' ? ' to NVENC' : '' }}
                </button>
            </div>
        </div>
        <div v-if="cfg.templates?.length" class="mb-2.5">
            <label>Template</label
            ><select v-model="templateId" @change="applyTemplate">
                <option value="">—</option>
                <option v-for="tp in cfg.templates" :key="tp.id" :value="tp.id">{{ tp.name }}</option>
            </select>
        </div>
    </div>
    <div v-if="broadcasters.length && !destEncodeIsTwitch" class="mb-2.5 rounded-md border border-line px-2.5 pt-2">
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2">
                <label>From a broadcaster</label
                ><select v-model="pickB" data-test="dest-broadcaster">
                    <option value="">—</option>
                    <option v-for="b in broadcasters" :key="b.id" :value="b.id">{{ b.name }}</option>
                </select>
            </div>
            <div class="mb-2">
                <label>Their feed</label
                ><select
                    :value="pickedB && dest.origin?.broadcaster === pickedB.id ? dest.origin.feed : ''"
                    :disabled="!pickedB"
                    data-test="dest-feed"
                    @change="pickFeed(($event.target as HTMLSelectElement).value)"
                >
                    <option value="" disabled>{{ pickedB?.feeds.length ? 'pick a feed' : 'no feeds' }}</option>
                    <option
                        v-for="f in pickedB?.feeds ?? []"
                        :key="f.id"
                        :value="f.id"
                        :disabled="f.delivery.kind === 'other'"
                    >
                        {{ f.description }}{{ f.delivery.kind === 'other' ? ' (notes only)' : '' }}
                    </option>
                </select>
            </div>
        </div>
        <p v-if="dest.origin" class="mt-0 mb-2 text-xs text-fg-2" data-test="dest-origin">
            Filled from <b>{{ dest.origin.broadcaster_name }}</b> · {{ dest.origin.feed_name }}: a copy, so later
            changes in the directory do not reach it.
            <button type="button" class="btn-ghost px-1 py-0 text-xs" @click="dest.origin = undefined">
                forget the origin
            </button>
            <template v-if="originFeed?.notes"><br />Their notes: {{ originFeed.notes }}</template>
        </p>
    </div>
    <div class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Kind</label
            ><select v-model="dest.kind">
                <template v-if="destEncodeIsTwitch">
                    <option value="twitch_eb">Twitch Enhanced Broadcasting</option>
                </template>
                <template v-else>
                    <option value="rtmp_push">RTMP push (we connect)</option>
                    <option value="srt_caller">SRT caller (we connect)</option>
                    <option value="rist">RIST (we send: connect or listen)</option>
                    <option value="rtmp_pull">RTMP pull (others connect to us)</option>
                    <option value="srt_listener">SRT listener (others connect to us)</option>
                    <option value="hls_pull">HLS origin (players and CDNs pull from us)</option>
                    <option value="udp">UDP, multicast or unicast (MPEG-TS)</option>
                    <option value="rtp">RTP (SMPTE 2022): FEC, two paths</option>
                    <option value="hls_push">HLS push (HTTPS: YouTube's HEVC, HDR and 4K)</option>
                </template>
                <option value="file">Record to disk</option>
            </select>
        </div>
        <div class="mb-2.5">
            <label>Delay (seconds, up to {{ cfg.max_buffer_seconds }})</label
            ><input v-model="dest.delay_seconds" type="number" min="0" :max="cfg.max_buffer_seconds" />
        </div>
    </div>
    <p v-if="dest.kind === 'twitch_eb'" class="mb-2.5 text-xs text-fg-2">
        Every rendition and the sound on one Enhanced RTMP connection, to the ingest and with the key Twitch gave the
        encode at its start (the encode holds the channel's stream key).
    </p>
    <div v-if="dest.kind === 'rtmp_push'">
        <div class="mb-2.5">
            <label>Server</label
            ><input
                v-model="rtmpServer"
                class="font-mono tabular-nums"
                placeholder="rtmp://live.twitch.tv/app"
                data-test="rtmp-server"
            />
        </div>
        <div class="mb-2.5">
            <label>Stream key</label><SecretInput v-model="rtmpKey" placeholder="live_…" data-test="rtmp-key" />
        </div>
    </div>
    <div v-else-if="dest.kind === 'hls_push'" class="mb-2.5">
        <label>Playlist URL <span class="text-fg-3">(with the stream key)</span></label
        ><SecretInput
            v-model="dest.url"
            placeholder="https://a.upload.youtube.com/http_upload_hls?cid=…&amp;copy=0&amp;file=index.m3u8"
            data-test="hls-url"
        />
        <p class="mt-1 text-xs text-fg-3">
            The node puts 2 s MPEG-TS segments and the playlist beside this URL by HTTP PUT. For YouTube: the stream
            key's HLS URL from YouTube Studio, ending in <code>file=index.m3u8</code> (the backup ingest is
            <code>b.upload…&amp;copy=1</code>, a second destination). H.264 or HEVC with AAC sound and a key frame at
            least every 4 s; HDR goes as HEVC 10-bit HLG or PQ.
        </p>
        <label v-if="destEncode?.video.ladder?.length || dest.adaptive" class="mt-2 flex items-center gap-1.5"
            ><input v-model="dest.adaptive" type="checkbox" class="w-auto" data-test="hls-adaptive" /> adaptive: every
            rendition of the encode's ladder ({{ (destEncode?.video.ladder?.length ?? 0) + 1 }}), under a master
            playlist at this URL</label
        >
        <p v-if="dest.adaptive" class="mt-1 text-xs text-fg-3">
            For a CDN's HLS ingest (one that takes PUT): the URL names the master playlist
            (<code>…/live/master.m3u8</code>); each rendition's playlist and segments go beside it. Not for YouTube,
            which takes one rendition and makes its own.
        </p>
        <div v-if="!dest.adaptive" class="mt-2">
            <label>Ad markers in the playlist</label
            ><select v-model="hlsCues" data-test="hls-cues">
                <option value="">none</option>
                <option value="cue_out">#EXT-X-CUE-OUT / CUE-IN (with the SCTE-35 cue in base64)</option>
                <option value="daterange">#EXT-X-DATERANGE with SCTE35-OUT / SCTE35-IN</option>
            </select>
            <p class="mt-1 text-xs text-fg-3">
                The encode's SCTE-35 cues (its ad breaks, or its source's) as tags on the segment nearest each splice,
                for an ingest that inserts ads from the playlist; the segments carry the cues too. Not for YouTube,
                which refuses a playlist with tags it does not know and takes ad breaks through its own API.
            </p>
        </div>
    </div>
    <div v-else-if="!isFile && !isPull && dest.kind !== 'twitch_eb'" class="mb-2.5">
        <label>URL</label
        ><input
            v-model="dest.url"
            class="font-mono tabular-nums"
            :placeholder="
                dest.kind === 'udp'
                    ? 'udp://239.1.1.1:5000'
                    : dest.kind === 'rtp'
                      ? 'rtp://239.1.1.1:5000'
                      : dest.kind === 'rist'
                        ? 'rist://receiver:5000 or rist://@:5000'
                        : 'srt://host:9000'
            "
        />
        <p v-if="dest.kind === 'rist'" class="mt-1 mb-0 text-xs text-fg-3">
            <code>rist://receiver:5000</code>: this node sends to a receiver that listens; <code>rist://@:5000</code>:
            this node listens on UDP 5000 and the receiver connects (open the port).
        </p>
    </div>
    <div v-if="node.pair" class="mb-2.5">
        <label>Sent by <span class="text-fg-3">(1+1)</span></label
        ><select v-model="dest.pair_only" data-test="pair-only">
            <option :value="undefined">both nodes</option>
            <option value="this">this node only</option>
            <option value="partner">{{ node.partner || 'its partner' }} only</option>
        </select>
    </div>
    <div
        v-if="hasPartner && dest.pair_only !== 'this' && dest.pair_only !== 'partner'"
        class="mb-2.5"
        data-test="partner-url"
    >
        <label
            >Where {{ node.partner || 'its 1+1 partner' }} sends
            <span class="text-fg-3">(1+1: a backup ingest; empty, the same as this node)</span></label
        >
        <SecretInput
            v-if="dest.kind === 'rtmp_push' || dest.kind === 'hls_push'"
            v-model="dest.partner_url"
            placeholder="rtmp://b.rtmp.youtube.com/live2?backup=1/…"
            data-test="partner-url-input"
        />
        <input v-else v-model="dest.partner_url" class="font-mono tabular-nums" data-test="partner-url-input" />
        <button
            v-if="ytBackup && dest.partner_url !== ytBackup"
            type="button"
            class="mt-1 btn-ghost text-xs"
            data-test="youtube-backup"
            @click="dest.partner_url = ytBackup"
        >
            YouTube's backup ingest
        </button>
    </div>
    <p v-if="twitchPaired && !dest.pair_only" class="mb-2.5 text-xs text-warn" data-test="twitch-paired">
        Twitch takes one stream per stream key: {{ node.partner || 'the partner' }}'s connection replaces this one's,
        and most channels have no backup ingest. Choose one node under <i>Sent by</i>; if it fails, choose the other.
    </p>
    <template v-if="dest.kind === 'udp' || dest.kind === 'rtp'">
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>TTL <span class="text-fg-3">(router hops; empty = 16)</span></label
                ><input v-model="dest.ttl" type="number" min="1" max="255" placeholder="16" />
            </div>
            <div class="mb-2.5">
                <label>Interface</label
                ><select v-model="dest.interface">
                    <option value="">automatic (routing table)</option>
                    <option v-for="a in ipv4" :key="a" :value="a">{{ a }}</option>
                </select>
            </div>
        </div>
        <p class="text-xs text-fg-3">
            MPEG-TS, 7 packets per datagram<template v-if="dest.kind === 'rtp'">
                in RTP (SMPTE 2022-2), on an even port: RTCP takes the next</template
            >. A multicast address (239.x.x.x) reaches every receiver on the network that joins it, player nodes
            included; multicast does not cross the internet.
        </p>
        <template v-if="dest.kind === 'rtp'">
            <label class="mb-1.5 flex items-center gap-1.5"
                ><input v-model="fecOn" type="checkbox" class="w-auto" data-test="rtp-fec" /> FEC (SMPTE 2022-1): the
                receiver rebuilds lost packets without asking</label
            >
            <div v-if="fecOn" class="grid grid-cols-2 gap-2.5">
                <div class="mb-2.5">
                    <label>Columns (L)</label><input v-model.number="fecCols" type="number" min="4" max="20" />
                </div>
                <div class="mb-2.5">
                    <label>Rows (D)</label><input v-model.number="fecRows" type="number" min="4" max="20" />
                </div>
            </div>
            <p v-if="fecOn" class="text-xs text-fg-3">
                Column FEC on the port +2, row FEC on +4: {{ fecCols }}×{{ fecRows }} adds about
                {{ Math.round(100 / Number(fecCols || 1) + 100 / Number(fecRows || 1)) }} % to the stream; at most 100
                packets in the matrix.
            </p>
            <label class="mb-1.5 flex items-center gap-1.5"
                ><input v-model="path2On" type="checkbox" class="w-auto" data-test="rtp-path2" /> a second path (SMPTE
                2022-7): every packet on both, the receiver keeps whichever arrives</label
            >
            <div v-if="path2On" class="grid grid-cols-2 gap-2.5">
                <div class="mb-2.5">
                    <label>Second path</label
                    ><input
                        v-model="dest.path2!.url"
                        class="font-mono tabular-nums"
                        placeholder="rtp://239.2.1.1:5000"
                    />
                </div>
                <div class="mb-2.5">
                    <label>From interface</label
                    ><select v-model="dest.path2!.interface">
                        <option value="">automatic (routing table)</option>
                        <option v-for="a in ipv4" :key="a" :value="a">{{ a }}</option>
                    </select>
                </div>
            </div>
        </template>
    </template>
    <template v-if="isPull">
        <p class="text-xs text-fg-2">
            This node serves the stream; the addresses to pull from appear in the detail panel once saved. Several
            clients can pull at once.
            <template v-if="dest.kind === 'hls_pull'">
                HLS in fragmented MP4 (H.264, HEVC or AV1, every audio track as a rendition), 2 s segments, over plain
                HTTP: put a CDN or a TLS proxy in front for the internet. Readers are not counted or capped.
            </template>
        </p>
        <div class="grid grid-cols-3 gap-2.5">
            <div class="mb-2.5">
                <label>Username <span class="text-fg-3">(optional)</span></label
                ><input v-model="pullUser" autocomplete="off" />
            </div>
            <div class="mb-2.5">
                <label>Password</label
                ><input
                    v-model="pullPass"
                    type="password"
                    class="font-mono tabular-nums"
                    autocomplete="new-password"
                    title="Kept on the node and never shown again: type a new one to change it, clear it to remove it"
                />
            </div>
            <div v-if="dest.kind !== 'hls_pull'" class="mb-2.5">
                <label>Max clients <span class="text-fg-3">(0 = no limit)</span></label
                ><input v-model="dest.max_clients" type="number" min="0" step="1" />
            </div>
        </div>
        <div class="mb-2.5">
            <label
                >Stream name
                <span class="text-fg-3"
                    >(what players type:
                    {{
                        dest.kind === 'rtmp_pull'
                            ? 'rtmp://node/'
                            : dest.kind === 'hls_pull'
                              ? 'http://node:port/'
                              : 'streamid=read:'
                    }}<b>{{ dest.stream || dest.id || slug(dest.name) || 'name' }}</b
                    >{{ dest.kind === 'hls_pull' ? '/index.m3u8' : '' }}; letters, digits . _ ~ -)</span
                ></label
            >
            <input
                v-model="dest.stream"
                class="max-w-[260px] font-mono tabular-nums"
                :placeholder="dest.id || slug(dest.name) || 'main'"
                :aria-invalid="!!streamProblem"
            />
            <div v-if="streamProblem" class="mt-0.5 text-xs text-bad">{{ streamProblem }}</div>
        </div>
        <div class="mb-2.5">
            <label
                >Port
                <span class="text-fg-3"
                    >({{ pullKind.proto.toUpperCase() }}; empty = node default, {{ pullKind.def }})</span
                ></label
            ><input
                v-model="dest.port"
                type="number"
                min="1"
                max="65535"
                step="1"
                :placeholder="String(pullKind.def)"
                class="max-w-[160px]"
            />
            <p class="text-xs" :style="{ color: portClash ? 'var(--bad)' : 'var(--fg-3)', margin: '4px 0 0' }">
                {{ portNote }}
            </p>
        </div>
    </template>
    <template v-if="isFile">
        <div class="mb-2.5">
            <label>Directory</label>
            <div class="flex items-center gap-1.5">
                <input
                    v-model="dest.path"
                    class="min-w-0 flex-1 font-mono tabular-nums"
                    placeholder="/var/lib/ese/recordings"
                /><button type="button" class="btn-ghost text-xs" @click="picking = !picking">browse…</button>
            </div>
            <p
                v-if="pathNote"
                class="text-xs"
                :style="{ margin: '4px 0 0', color: pathNote.bad ? 'var(--bad)' : 'var(--fg-3)' }"
            >
                {{ pathNote.text }}
            </p>
            <FolderPicker
                v-if="picking"
                :start="dest.path || '/var/lib/ese/recordings'"
                @pick="
                    (p) => {
                        dest.path = p
                        picking = false
                        checkPath()
                    }
                "
                @close="picking = false"
            />
        </div>
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Format</label>
                <select v-model="dest.container">
                    <option :value="undefined">MPEG-TS (.ts)</option>
                    <option value="mp4" :disabled="destEncodeIsPassthrough">
                        MP4 (.mp4){{ destEncodeIsPassthrough ? ' · not for a passthrough' : '' }}
                    </option>
                </select>
            </div>
            <div class="mb-2.5">
                <label>Files</label>
                <select v-model="dest.open_ended">
                    <option :value="undefined">chunks of a fixed length</option>
                    <option :value="true">one file per recording</option>
                </select>
            </div>
        </div>
        <div class="grid grid-cols-2 gap-2.5">
            <div v-if="!dest.open_ended" class="mb-2.5">
                <label>Chunk length (seconds)</label><input v-model="dest.segment_seconds" type="number" min="10" />
            </div>
            <div class="mb-2.5">
                <label>Keep (days, 0 = forever)</label><input v-model="dest.keep_days" type="number" min="0" />
            </div>
        </div>
        <p class="text-xs text-fg-3">
            {{ dest.container === 'mp4' ? 'MP4 is written as fragmented MP4' : 'MPEG-TS' }}: a file cut short by a crash
            or power loss still plays up to the last second or so.
            {{
                dest.open_ended
                    ? 'One file from start to stop, named by its start time; a restart of the recording (a reconnect, a delay change) begins a new file.'
                    : 'Every closed chunk plays on its own.'
            }}<template v-if="dest.container === 'mp4'">
                Some editors want a plain MP4:
                <span class="font-mono tabular-nums">ffmpeg -i in.mp4 -c copy out.mp4</span> rewrites one in
                seconds.</template
            >
        </p>
    </template>
    <RistOptions v-if="dest.kind === 'rist'" v-model="dest.rist!" />
    <details v-if="dest.kind === 'srt_caller' || dest.kind === 'srt_listener'" open>
        <summary>
            SRT options<span v-if="dest.kind === 'srt_listener'" class="text-fg-3">
                · readers must use the same passphrase</span
            >
        </summary>
        <div class="grid grid-cols-4 gap-2.5">
            <div v-if="dest.kind === 'srt_caller'" class="mb-2.5">
                <label>Latency (ms)</label><input v-model="dest.srt!.latency_ms" type="number" />
            </div>
            <div v-if="dest.kind === 'srt_caller'" class="mb-2.5">
                <label>Bitrate (kbit/s)</label
                ><input v-model="dest.srt!.bitrate_k" type="number" min="0" step="1000" placeholder="25000" />
            </div>
            <div class="mb-2.5">
                <label>Passphrase</label
                ><input
                    v-model="dest.srt!.passphrase"
                    type="password"
                    autocomplete="new-password"
                    title="Kept on the node and never shown again: type a new one to change it, clear it to remove it"
                />
            </div>
            <div v-if="dest.kind === 'srt_caller'" class="mb-2.5">
                <label>Stream ID</label><input v-model="dest.srt!.streamid" />
            </div>
        </div>
        <!-- the node offers a listener's readers 20 ms on our mediamtx build, 120 on
             stock; SRT takes the larger of the two ends' (DECISIONS 2026-10-18
             "SRT listener: a 20 ms floor") -->
        <p v-if="dest.kind === 'srt_caller'" class="mt-0 text-xs text-fg-3">
            The bitrate sizes SRT's buffers for the latency: the most the link will carry, 25 Mbit/s if empty.
        </p>
        <p v-if="dest.kind === 'srt_listener'" class="mt-0 text-xs text-fg-3" data-test="srt-listener-latency">
            Each reader sets its own latency: <span class="font-mono">latency=</span> in its URL (microseconds in
            ffmpeg, milliseconds in VLC), the Latency field in vMix. The node offers 20 ms (120 ms where its mediamtx is
            the stock one: the detail panel says which), so a reader on a LAN can ask that little; on a long or lossy
            path ask more, e.g. 120 ms (<span class="font-mono">latency=120000</span> in ffmpeg).
        </p>
    </details>
    <ScheduleEditor v-model="dest.schedule" what="it" />
    <p v-if="isNew" class="text-xs text-fg-3">
        New destinations start disabled. Enable from the detail panel when the target is ready, or give it a schedule.
    </p>
</template>
